import {Injectable} from '@angular/core';
import {UtilService} from './util.service';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import {Treasury} from '../model/Treasury';
import {TreasuryLog} from '../model/TreasuryLog';
import {TreasuryLogDoc} from '../model/TreasuryLogDoc';
import {Closure} from '../model/Closure';
import {Currency} from '../model/Currency';
import {PaymentProcess} from '../model/PaymentProcess';
import {TreasuryDocItem} from '../model/TreasuryDocItem';
import {Bus} from '../model/Bus';
import {User} from '../model/User';
import {Mrt} from '../model/Mrt';

@Injectable()
export class TreasuryService {

  constructor(private http: HttpClient, private utilService: UtilService) {
  }

  getTreasuries(): Observable<Treasury[]> {
    const url = this.utilService.getApiUrl('treasury/');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new Treasury(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getClosures(): Observable<Closure[]> {
    const url = this.utilService.getApiUrl('closure/');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new Closure(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getCurrencies(): Observable<Currency[]> {
    const url = this.utilService.getApiUrl('currency/active');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new Currency(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getBuses(filter: string): Observable<Bus[]> {
    const url = this.utilService.getApiUrl('buses/search?term=' + filter);
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new Bus(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getUsers(filter: string): Observable<User[]> {
    const url = this.utilService.getApiUrl('users/search?term=' + filter);
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new User(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getPaymentMethods(): Observable<string[]> {
    const url = this.utilService.getApiUrl('payment/methods');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getPaymentProcesses(): Observable<PaymentProcess[]> {
    const url = this.utilService.getApiUrl('payment/processes');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new PaymentProcess(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getTreasuryDocItems(paymentProcessId: number): Observable<TreasuryDocItem[]> {
    const url = this.utilService.getApiUrl('treasury-doc-items/for-payment-process/' + paymentProcessId);
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new TreasuryDocItem(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getTreasuryLogs(treasuryId: number): Observable<TreasuryLog[]> {
    const url = this.utilService.getApiUrl('treasury/' + treasuryId + '/logs');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new TreasuryLog(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getTreasuryLogDocs(treasuryLogId: number): Observable<TreasuryLogDoc[]> {
    const url = this.utilService.getApiUrl('treasury/log/' + treasuryLogId + '/docs');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new TreasuryLogDoc(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getActiveTreasuryLog(treasuryId: number): Observable<TreasuryLog> {
    const url = this.utilService.getApiUrl('treasury/' + treasuryId + '/active-logs');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return new TreasuryLog(response.data);
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  createTreasuryLog(treasuryId: number): Observable<any> {
    const url = this.utilService.getApiUrl('treasury/' + treasuryId + '/active-log/create');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  closeTreasuryLog(treasuryId: number): Observable<any> {
    const url = this.utilService.getApiUrl('treasury/' + treasuryId + '/active-log/close');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  createTreasuryLogDoc(treasuryId: number, treasuryLogDoc: TreasuryLogDoc): Observable<any> {
    const url = this.utilService.getApiUrl('treasury/' + treasuryId + '/log/doc-create');
    return this.http.post(url, treasuryLogDoc)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  cancelTreasuryLogDoc(treasuryLogDocId: number): Observable<any> {
    const url = this.utilService.getApiUrl('treasury/log/doc/' + treasuryLogDocId + '/cancellation');
    return this.http.post(url, null)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  getMrts(): Observable<Mrt[]> {
    const url = this.utilService.getApiUrl('mrt/');

    return this.http.get(url)
      .map((response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/treasury.service.ts